import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { registriereZurueck } from '../../services/appUmfang';
import { hubOverlayZurueck } from './projectHubLogic';

// Gemeinsamer Vollbild-Overlay-Rahmen für die ProjectHub-Dialoge (Karte, Sendeverlauf).
// Mit title: dunkle Kopfzeile mit Schließen-Button; ohne: schwebender Schließen-Button.

interface Props {
    widthClass: string;
    heightClass: string;
    title?: string;
    onClose: () => void;
    /**
     * Android-Zurück schließt wie der Schließen-Knopf — nur für Inhalte ohne
     * eigene Eingaben (Sendeverlauf). Sonst bleibt die Taste stehen wie bisher:
     * auf der Karte ginge ein gesetzter, nicht übernommener Standort verloren.
     */
    zurueckSchliesst?: boolean;
    children: React.ReactNode;
}

export const HubOverlay: React.FC<Props> = ({ widthClass, heightClass, title, onClose, zurueckSchliesst = false, children }) => {
    const flaecheRef = useRef<HTMLDivElement>(null);

    // Immer der neueste Rückruf, ohne die Zurück-Taste neu anzumelden.
    const schliessenRef = useRef(onClose);
    useEffect(() => {
        schliessenRef.current = onClose;
    });

    // Android-Zurück (nur in der App wirksam): wie der Schließen-Knopf. Ist
    // darin ein eigener Dialog offen, bleibt die Taste stehen wie bisher.
    useEffect(() => {
        if (!zurueckSchliesst) return;
        return registriereZurueck(() => {
            hubOverlayZurueck(flaecheRef.current, () => schliessenRef.current());
        }, () => flaecheRef.current);
    }, [zurueckSchliesst]);

    return (
        <div ref={flaecheRef} className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className={`bg-white rounded-2xl shadow-2xl w-full ${widthClass} ${heightClass} flex flex-col overflow-hidden relative border border-slate-200`}>
                {title ? (
                    <>
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg">{title}</h3>
                            <button onClick={onClose} aria-label="Schließen" className="hover:bg-white/10 p-1 rounded flex items-center justify-center max-md:min-h-11 max-md:min-w-11"><X className="w-6 h-6"/></button>
                        </div>
                        <div className="flex-1 overflow-hidden">{children}</div>
                    </>
                ) : (
                    <>
                        <div className="absolute top-4 right-4 z-50">
                            <button onClick={onClose} aria-label="Schließen" className="bg-white p-2 rounded-full shadow-md hover:bg-slate-100 border border-slate-200 flex items-center justify-center max-md:min-h-11 max-md:min-w-11"><X className="w-5 h-5 text-slate-500"/></button>
                        </div>
                        {children}
                    </>
                )}
            </div>
        </div>
    );
};
